"use client";

import Image from "next/image";
import { useState } from "react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import ModernDialog from "@/components/ui/ModernDialog";

function formatIDR(amount) {
    return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
    }).format(amount);
}

export default function InvitationCard({ invitation, priority = false }) {
    const { items, addItem, replaceItem, setOpen } = useCart();
    const { showToast } = useAuth();
    const [showConfirm, setShowConfirm] = useState(false);

    const inCart = items.some((i) => i.id === invitation.id);
    const hasOther = items.length > 0 && !inCart;

    const handleAdd = () => {
        if (inCart) {
            setOpen(true);
            return;
        }
        if (hasOther) {
            setShowConfirm(true);
            return;
        }
        addItem({ ...invitation, type: "invitation" });
        showToast(`"${invitation.name}" ditambahkan ke keranjang.`);
    };

    const handleConfirmReplace = () => {
        replaceItem({ ...invitation, type: "invitation" });
        showToast(`Template diganti ke "${invitation.name}".`);
        setShowConfirm(false);
    };

    return (
        <>
            <ModernDialog
                isOpen={showConfirm}
                onClose={() => setShowConfirm(false)}
                title="Ganti template?"
                description={`Keranjang kamu sudah berisi template lain. Menambahkan "${invitation.name}" akan mengganti template yang ada.`}
            >
                <div className="flex gap-3 mt-2">
                    <button
                        onClick={() => setShowConfirm(false)}
                        className="flex-1 cursor-pointer rounded-full border border-zinc-200 py-2.5 text-sm font-semibold text-zinc-700 transition hover:border-zinc-300 hover:bg-zinc-50"
                    >
                        Batal
                    </button>
                    <button
                        onClick={handleConfirmReplace}
                        className="flex-1 cursor-pointer rounded-full bg-primary py-2.5 text-sm font-semibold text-white transition hover:bg-primary-dark"
                    >
                        Ganti
                    </button>
                </div>
            </ModernDialog>

            <div className="group flex flex-col rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden transition hover:shadow-md hover:border-primary-light">
                {/* Image — 1:1 square */}
                <div className="relative aspect-square w-full bg-primary-light overflow-hidden">
                    {invitation.image || invitation.image_url ? (
                        <Image
                            src={invitation.image || invitation.image_url}
                            alt={invitation.name}
                            fill
                            priority={priority}
                            loading={priority ? "eager" : "lazy"}
                            className="object-cover transition-transform duration-500 group-hover:scale-105"
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        />
                    ) : (
                        <div className="h-full w-full bg-primary-light" />
                    )}
                </div>

                {/* Content */}
                <div className="flex flex-1 flex-col gap-2 p-5">
                    <h3 className="font-cormorant text-2xl font-semibold text-zinc-900">
                        {invitation.name}
                    </h3>
                    <p className="text-sm text-zinc-500 line-clamp-2 flex-1">
                        {invitation.description}
                    </p>

                    <div className="mt-4 flex items-center justify-between gap-3">
                        <span className="text-base font-semibold text-primary-dark">
                            {formatIDR(invitation.price)}
                        </span>
                        <div className="flex gap-2">
                            <a
                                href={`/templates/${invitation.slug}/preview`}
                                className="cursor-pointer rounded-full border border-primary-light px-4 py-1.5 text-xs font-semibold text-primary-dark transition hover:border-primary"
                            >
                                Preview
                            </a>
                            <button
                                onClick={handleAdd}
                                className={`cursor-pointer rounded-full px-4 py-1.5 text-xs font-semibold transition ${inCart
                                        ? "bg-primary-dark text-white hover:bg-primary"
                                        : "bg-primary text-white hover:bg-primary-dark"
                                    }`}
                            >
                                {inCart ? "Di Keranjang" : "Tambah"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
